import { Button, Divider, Flex, Grid, Loader, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconEyeOff } from '@tabler/icons-react';
import {
  Claim,
  CreateClaimForm,
  Debit,
  EditClaimForm,
  EOB,
  Healthcard,
  Patient,
  Provider,
  Site,
  Status,
  Submission,
  Tariff,
} from 'interfaces';
import { ReactNode } from 'react';
import { FormType } from 'pages/claims/components';

type ClaimKeys = keyof Claim;
type SiteKeys = `site.${keyof Site}`;
type PatientKeys = `patient.${keyof Patient}`;
type ProviderKeys = `provider.${keyof Provider}`;
type TariffKeys = `tariffs.${keyof Tariff}`;
type StatusKeys = `status.${keyof Status}`;
type HealthcardKeys = `healthcard.${keyof Healthcard}`;
type DebitKeys = `debits.${keyof Debit}`;
type EOBKeys = `eobs.${keyof EOB}`;
type SubmissionKeys = `submissions.${keyof Submission}`;
type CreditKeys = 'credit.amount';

export type AllKeys =
  | ClaimKeys
  | SiteKeys
  | PatientKeys
  | ProviderKeys
  | TariffKeys
  | StatusKeys
  | HealthcardKeys
  | DebitKeys
  | EOBKeys
  | SubmissionKeys
  | CreditKeys
  | 'patientIDs'
  | 'row-state'
  | 'serviceTime'
  | 'outstandingAmount'
  | 'claimComments'
  | '';

interface DataSectionProps {
  index?: string;
  form?: UseFormReturnType<EditClaimForm> | UseFormReturnType<CreateClaimForm>;
  keys: AllKeys[];
  cols: number;
  grow?: boolean;
  editComponentsMap: Record<string, ReactNode>;
  type?: FormType;
}

export interface SectionProps extends DataSectionProps {
  title: string;
  icon?: React.ReactNode;
  className?: string;
  customStyle?: React.CSSProperties;
  isLoading?: boolean;
  hideLogic?: {
    show: boolean;
    onToggle: () => void;
  };
  focusRef?: React.RefObject<HTMLDivElement>;
  hideLabel?: boolean;
  children?: ReactNode;
}

const DataSection: React.FC<DataSectionProps> = ({
  keys = [],
  index,
  cols,
  grow = false,
  editComponentsMap,
  type,
}) => {
  return (
    <Grid
      gutter={{
        base: 'xs',
        sm: 'sm',
        lg: 'lg',
      }}
      w={'100%'}
      justify="space-around"
      grow={grow}
    >
      {(type === 'edit' || type === 'bulkEdit') &&
        keys
          .filter((item) => item !== 'provider')
          .map((key) => (
            <Grid.Col
              key={key}
              span={{
                base: 12,
                sm: 'content',
                lg: 12 / cols,
              }}
            >
              {editComponentsMap[`${index}_${key}`]}
            </Grid.Col>
          ))}
      {(type === 'create' || type === 'bulk') &&
        keys.map((key) => (
          <Grid.Col
            key={key}
            span={{
              base: 12,
              sm: 'content',
              lg: 12 / cols,
            }}
          >
            {editComponentsMap[`${'mrt-row-create'}_${key}`]}
          </Grid.Col>
        ))}
    </Grid>
  );
};

const SectionLabel = ({
  title,
  icon,
  hideLogic,
}: {
  title: string;
  icon?: React.ReactNode;
  hideLogic?: {
    show: boolean;
    onToggle: () => void;
  };
}) => {
  return (
    <Flex
      className="section-label"
      w={'5%'}
      justify={'space-evenly'}
      align={'center'}
      miw={'3.125rem'}
    >
      <Flex direction={'column'} align={'center'}>
        {icon}
        <Title order={6} style={{ textAlign: 'center' }}>
          {title}
        </Title>
        {hideLogic && (
          <Flex direction={'column'} align={'center'}>
            <Button
              leftSection={<IconEyeOff size={14} />}
              variant="subtle"
              onClick={hideLogic.onToggle}
            >
              Hide
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export const Section: React.FC<SectionProps> = ({
  keys,
  index,
  title,
  icon,
  className,
  cols = 5,
  customStyle = null,
  editComponentsMap,
  type = 'edit',
  isLoading = false,
  hideLogic = null,
  hideLabel = false,
  children,
}) => {
  return (
    <Flex
      className={className}
      gap={'md'}
      style={{
        ...customStyle,
      }}
      w="100%"
    >
      {!hideLabel && (
        <>
          <SectionLabel title={title} icon={icon} hideLogic={hideLogic} />
          <Flex w={'1%'} justify={'center'} gap={'md'}>
            <Divider variant="solid" orientation="vertical" />
          </Flex>
        </>
      )}
      {isLoading ? (
        <Flex
          w={'100%'}
          justify={'center'}
          align={'center'}
          style={{ height: '100%' }}
        >
          <Loader variant="bars" />
        </Flex>
      ) : children ? (
        children
      ) : (
        <DataSection
          keys={keys}
          index={index}
          cols={cols}
          editComponentsMap={editComponentsMap}
          type={type}
        />
      )}
    </Flex>
  );
};
